import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import {
  authService,
  AdminViewAsUserOption,
  AuthSessionError,
  User,
  AuthResponse,
  LoginCredentials,
  SignupCredentials,
} from "@/features/auth/services/authService";
import type { SelfServicePlanTier } from "@/shared/apiTypes";
import {
  clearAdminViewAsUserId,
  getAdminViewAsUserId,
  setAdminViewAsUserId as persistAdminViewAsUserId,
} from "@/features/auth/services/adminViewAs";
import { log } from "@/shared/utils/logger";
import i18n from "@/shared/i18n/config";
import { startAuditSession, stopAuditSession, trackAuditEvent } from "@/shared/audit/auditClient";

// Re-export types for other modules
export type { User };
import {
  PermissionService,
  PermissionStructure,
  updatePermissionService,
} from "@/features/auth/services/PermissionService";

interface AuthContextType {
  user: User | null;
  actualUser: User | null;
  viewAsUser: User | null;
  viewAsUserId: string | null;
  viewAsOptions: AdminViewAsUserOption[];
  isViewingAs: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  setViewAsUserId: (userId: string | null) => Promise<void>;
  clearViewAs: () => void;
  login: (credentials: LoginCredentials) => Promise<void>;
  signup: (credentials: SignupCredentials) => Promise<AuthResponse>;
  updateSelfServicePlan: (selectedPlan: SelfServicePlanTier) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (
    resource: string,
    action: string,
    view?: string | null
  ) => boolean;
  canAccessView: (viewName: string) => boolean;
  validateOperation: (operation: string) => boolean;
  shouldShowUIElement: (permission: string) => boolean;
  getAllPermissions: () => PermissionStructure;
  permissionService: PermissionService | null;
  hasViewPermission: (
    view: string,
    resource: string,
    action: string
  ) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [actualUser, setActualUser] = useState<User | null>(() =>
    authService.isAuthenticated() ? authService.getUser() : null
  );
  const [viewAsUser, setViewAsUser] = useState<User | null>(null);
  const [viewAsUserId, setViewAsUserIdState] = useState<string | null>(() => getAdminViewAsUserId());
  const [viewAsOptions, setViewAsOptions] = useState<AdminViewAsUserOption[]>([]);
  const [viewAsRefreshNonce, setViewAsRefreshNonce] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(() =>
    authService.isAuthenticated()
  );
  const [isLoading, setIsLoading] = useState(true);
  const [permissionService, setPermissionService] =
    useState<PermissionService | null>(null);
  const authReloadPromiseRef = useRef<Promise<void> | null>(null);
  const authReloadForceLogoutRef = useRef(false);
  const authStateEpochRef = useRef(0);
  const logoutInFlightRef = useRef(false);
  const actualUserCanUseViewAs = actualUser?.godMode === true;
  const user = viewAsUser ?? actualUser;
  const isViewingAs = Boolean(viewAsUserId && actualUserCanUseViewAs);

  useEffect(() => {
    if (actualUser) {
      startAuditSession(actualUser);
      return () => {
        stopAuditSession(actualUser.id);
      };
    }
    stopAuditSession();
    return undefined;
  }, [actualUser]);

  // Initialize permission service when user changes
  useEffect(() => {
    const service = new PermissionService(user);
    setPermissionService(service);
    // Update global permission service
    updatePermissionService(user);
  }, [user]);

  useEffect(() => {
    // Check if user is still authenticated on mount
    const checkAuth = async () => {
      const bootstrapEpoch = authStateEpochRef.current;
      const isStaleBootstrap = () => authStateEpochRef.current !== bootstrapEpoch;
      if (logoutInFlightRef.current) {
        setActualUser(null);
        setViewAsUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }
      log.info("AuthContext", "Checking authentication on mount...");
      try {
        const token = authService.getToken();
        log.debug("AuthContext", "Token present:", !!token);

        const reloadedUser = await authService.reloadSession();
        if (reloadedUser) {
          if (isStaleBootstrap()) {
            return;
          }
          log.info("AuthContext", "Reloaded authenticated session on mount");
          setActualUser(reloadedUser);
          setIsAuthenticated(true);
          return;
        }

        if (isStaleBootstrap()) {
          return;
        }
        log.info("AuthContext", "No active session found on mount");
        setActualUser(null);
        setViewAsUser(null);
        setIsAuthenticated(false);
      } catch (error) {
        log.error("AuthContext", "Auth check failed:", error);
        if (error instanceof AuthSessionError && error.kind === "transient") {
          const existingUser = authService.getUser();
          if (existingUser && authService.isAuthenticated()) {
            log.warn(
              "AuthContext",
              "Preserving existing session during transient auth reload failure"
            );
            if (isStaleBootstrap()) {
              return;
            }
            setActualUser(existingUser);
            setIsAuthenticated(true);
            return;
          }
        }
        if (isStaleBootstrap()) {
          return;
        }
        setActualUser(null);
        setViewAsUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const reloadAuthState = useCallback(async (forceLogoutOnFailure = false) => {
    if (logoutInFlightRef.current) {
      setActualUser(null);
      setViewAsUser(null);
      setIsAuthenticated(false);
      return;
    }
    if (authReloadPromiseRef.current) {
      if (forceLogoutOnFailure) {
        authReloadForceLogoutRef.current = true;
      }
      return authReloadPromiseRef.current;
    }

    const reloadEpoch = authStateEpochRef.current;
    const isStaleReload = () =>
      logoutInFlightRef.current || authStateEpochRef.current !== reloadEpoch;
    authReloadForceLogoutRef.current = forceLogoutOnFailure;

    const reloadPromise = Promise.resolve().then(async () => {
      try {
        const refreshedUser = await authService.reloadSession();
        if (refreshedUser) {
          if (isStaleReload()) {
            return;
          }
          setActualUser(refreshedUser);
          setIsAuthenticated(true);
          return;
        }

        if (isStaleReload()) {
          return;
        }

        // A null reload is a terminal no-session result. Transient failures
        // throw and retain their existing recovery behavior in the catch below.
        setActualUser(null);
        setViewAsUser(null);
        setIsAuthenticated(false);
      } catch (error) {
        log.warn("AuthContext", "Live auth reload failed:", error);

        if (
          error instanceof AuthSessionError &&
          error.kind === "transient" &&
          !authReloadForceLogoutRef.current
        ) {
          const existingUser = authService.getUser();
          if (existingUser) {
            if (isStaleReload()) {
              return;
            }
            setActualUser(existingUser);
            setIsAuthenticated(true);
            return;
          }
        }

        if (isStaleReload()) {
          return;
        }

        if (authReloadForceLogoutRef.current) {
          try {
            await authService.logout();
          } catch { /* logout best-effort */ }
          if (isStaleReload()) {
            return;
          }
          setActualUser(null);
          setViewAsUser(null);
          setIsAuthenticated(false);
          return;
        }

        const currentUser = await authService.getCurrentUser();
        if (isStaleReload()) {
          return;
        }
        setActualUser(currentUser);
        setIsAuthenticated(!!currentUser);
      } finally {
        authReloadPromiseRef.current = null;
        authReloadForceLogoutRef.current = false;
      }
    });

    authReloadPromiseRef.current = reloadPromise;
    return reloadPromise;
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const isCurrentAuthEvent = (event: Event) => {
      const detail = event instanceof CustomEvent
        ? (event.detail as { userId?: string; user_id?: string } | undefined)
        : undefined;
      const affectedUserId = detail?.userId || detail?.user_id;
      return !affectedUserId || affectedUserId === actualUser?.id || (
        actualUserCanUseViewAs && affectedUserId === viewAsUserId
      );
    };

    const handleAuthUpdated = (event: Event) => {
      if (logoutInFlightRef.current || !isCurrentAuthEvent(event)) {
        return;
      }
      const detail = event instanceof CustomEvent
        ? (event.detail as { userId?: string; user_id?: string; type?: string } | undefined)
        : undefined;
      const affectedUserId = detail?.userId || detail?.user_id || null;
      if (
        actualUserCanUseViewAs &&
        viewAsUserId &&
        (!affectedUserId || affectedUserId === viewAsUserId)
      ) {
        setViewAsRefreshNonce((current) => current + 1);
      }
      void reloadAuthState(false);
    };

    const handleAuthInvalidated = (event: Event) => {
      if (logoutInFlightRef.current || !isCurrentAuthEvent(event)) {
        return;
      }
      void reloadAuthState(true);
    };

    window.addEventListener("auth-updated", handleAuthUpdated);
    window.addEventListener("project_access_updated", handleAuthUpdated);
    window.addEventListener("auth-invalidated", handleAuthInvalidated);

    return () => {
      window.removeEventListener("auth-updated", handleAuthUpdated);
      window.removeEventListener("project_access_updated", handleAuthUpdated);
      window.removeEventListener("auth-invalidated", handleAuthInvalidated);
    };
  }, [actualUser?.id, actualUserCanUseViewAs, reloadAuthState, viewAsUserId]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!actualUser) {
      clearAdminViewAsUserId();
      setViewAsUserIdState(null);
      setViewAsUser(null);
      setViewAsOptions([]);
      return;
    }

    if (!actualUserCanUseViewAs) {
      clearAdminViewAsUserId();
      setViewAsUserIdState(null);
      setViewAsUser(null);
      setViewAsOptions([]);
      return;
    }

    let cancelled = false;
    authService
      .listAdminViewAsUsers()
      .then((users) => {
        if (!cancelled) {
          setViewAsOptions(users);
        }
      })
      .catch((error) => {
        log.warn("AuthContext", "Failed to load admin view-as users:", error);
        if (!cancelled) {
          setViewAsOptions([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [actualUser, actualUserCanUseViewAs, isLoading]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!actualUserCanUseViewAs || !viewAsUserId) {
      setViewAsUser(null);
      return;
    }

    let cancelled = false;
    authService
      .getAdminViewAsUser(viewAsUserId)
      .then((selectedUser) => {
        if (!cancelled) {
          setViewAsUser(selectedUser);
          window.dispatchEvent(new CustomEvent("admin-view-as-updated", {
            detail: { userId: selectedUser.id },
          }));
        }
      })
      .catch((error) => {
        log.warn("AuthContext", "Failed to load admin view-as target:", error);
        const status = Number((error as { status?: number } | null)?.status || 0);
        if (!cancelled && (status === 403 || status === 404)) {
          clearAdminViewAsUserId();
          setViewAsUserIdState(null);
        }
        if (!cancelled) {
          setViewAsUser(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [actualUserCanUseViewAs, isLoading, viewAsRefreshNonce, viewAsUserId]);

  const login = async (credentials: LoginCredentials) => {
    authStateEpochRef.current += 1;
    const loginEpoch = authStateEpochRef.current;
    authReloadForceLogoutRef.current = false;
    logoutInFlightRef.current = false;
    log.info("AuthContext", "Login request:", {
      username: credentials.username,
      rememberMe: credentials.rememberMe,
    });
    const response = await authService.login(credentials);
    if (authStateEpochRef.current !== loginEpoch) return;
    clearAdminViewAsUserId();
    setViewAsUserIdState(null);
    setViewAsUser(null);
    setActualUser(response.user);
    setIsAuthenticated(true);
    startAuditSession(response.user);
    trackAuditEvent({
      eventType: "auth.login_succeeded",
      category: "auth",
      metadata: { username: response.user.username, role: response.user.role },
    });
  };

  const signup = async (credentials: SignupCredentials) => {
    authStateEpochRef.current += 1;
    const signupEpoch = authStateEpochRef.current;
    authReloadForceLogoutRef.current = false;
    logoutInFlightRef.current = false;
    log.info("AuthContext", "Signup request:", {
      email: credentials.email,
      rememberMe: credentials.rememberMe,
    });
    const response = await authService.signup(credentials);
    if (authStateEpochRef.current !== signupEpoch) return response;
    clearAdminViewAsUserId();
    setViewAsUserIdState(null);
    setViewAsUser(null);
    setActualUser(response.user);
    setIsAuthenticated(true);
    startAuditSession(response.user);
    trackAuditEvent({
      eventType: "auth.signup_succeeded",
      category: "auth",
      metadata: { username: response.user.username, role: response.user.role },
    });
    return response;
  };

  const updateSelfServicePlan = async (selectedPlan: SelfServicePlanTier) => {
    const response = await authService.updateSelfServicePlan(selectedPlan);
    setActualUser(response.user);
    setIsAuthenticated(true);
    window.dispatchEvent(new CustomEvent("auth-updated", {
      detail: { userId: response.user.id, type: "self_service_plan" },
    }));
  };

  const logout = async () => {
    authStateEpochRef.current += 1;
    const logoutEpoch = authStateEpochRef.current;
    logoutInFlightRef.current = true;
    authReloadPromiseRef.current = null;
    authReloadForceLogoutRef.current = false;
    trackAuditEvent({ eventType: "auth.logout_requested", category: "auth" });
    stopAuditSession(actualUser?.id);
    clearAdminViewAsUserId();
    setViewAsUserIdState(null);
    setViewAsUser(null);
    setActualUser(null);
    setIsAuthenticated(false);
    try {
      await authService.logout();
    } catch {
      if (authStateEpochRef.current === logoutEpoch && typeof window !== "undefined") {
        window.alert(
          i18n.t("Sign-out could not be confirmed. Please retry before leaving this device.")
        );
      }
    } finally {
      // A login that began while the revocation request was in flight owns the
      // new state. The old logout must not erase that later principal.
      if (authStateEpochRef.current === logoutEpoch) {
        clearAdminViewAsUserId();
        setViewAsUserIdState(null);
        setViewAsUser(null);
        setActualUser(null);
        setIsAuthenticated(false);
        logoutInFlightRef.current = false;
      }
    }
  };

  const setViewAsUserId = useCallback(async (nextUserId: string | null) => {
    if (!nextUserId) {
      clearAdminViewAsUserId();
      setViewAsUserIdState(null);
      setViewAsUser(null);
      window.dispatchEvent(new CustomEvent("admin-view-as-updated", {
        detail: { userId: null },
      }));
      return;
    }
    if (!actualUserCanUseViewAs) {
      clearAdminViewAsUserId();
      setViewAsUserIdState(null);
      setViewAsUser(null);
      window.dispatchEvent(new CustomEvent("admin-view-as-updated", {
        detail: { userId: null },
      }));
      return;
    }

    persistAdminViewAsUserId(nextUserId);
    setViewAsUser(null);
    setViewAsUserIdState(nextUserId);
    setViewAsRefreshNonce((current) => current + 1);
    window.dispatchEvent(new CustomEvent("admin-view-as-updated", {
      detail: { userId: nextUserId, pending: true },
    }));
  }, [actualUserCanUseViewAs]);

  const clearViewAs = useCallback(() => {
    void setViewAsUserId(null);
  }, [setViewAsUserId]);

  // Systematic permission validation using new service
  const hasPermission = (
    resource: string,
    action: string,
    view: string | null = null
  ): boolean => {
    // ADMIN BYPASS: Admin users have unrestricted access to EVERYTHING
    if (user?.role === "admin") return true;
    if (!permissionService) return false;
    return permissionService.hasPermission(resource, action, view);
  };

  // View-specific permission check
  const hasViewPermission = (
    view: string,
    resource: string,
    action: string
  ): boolean => {
    // ADMIN BYPASS: Admin users have unrestricted access to EVERYTHING
    if (user?.role === "admin") return true;
    if (!permissionService) return false;
    return permissionService.hasPermission(resource, action, view);
  };

  const canAccessView = (viewName: string): boolean => {
    // ADMIN BYPASS: Admin users can access all views
    if (user?.role === "admin") return true;
    if (!permissionService) return false;
    return permissionService.canAccessView(viewName);
  };

  const validateOperation = (operation: string): boolean => {
    // ADMIN BYPASS: Admin users can perform all operations
    if (user?.role === "admin") return true;
    if (!permissionService) return false;
    return permissionService.validateOperation(operation);
  };

  const shouldShowUIElement = (permission: string): boolean => {
    // ADMIN BYPASS: Admin users can see all UI elements
    if (user?.role === "admin") return true;
    if (!permissionService) return false;
    return permissionService.shouldShowUIElement(permission);
  };

  const getAllPermissions = (): PermissionStructure => {
    // ADMIN BYPASS: Admin users have ALL permissions
    if (user?.role === "admin") {
      return {
        tasks: { create: true, read: true, update: true, delete: true },
        documents: { create: true, read: true, update: true, delete: true },
        calendar: { create: true, read: true, update: true, delete: true },
        users: { create: true, read: true, update: true, delete: true },
        agents: { execute: true },
        feedback: { read: true, create: true, updateOwn: true, resolve: true, manage: true, export: true },
        views: {
          dashboard: true,
          kanban: true,
          gantt: true,
          calendar: true,
          docs: true,
          reports: true,
          agentOffice: true,
          upgrade: true,
          settings: true,
          admin: true,
        },
        system: {
          import: true,
          export: true,
          history: true,
          search: true,
          customize: true,
        },
      };
    }

    if (!permissionService) {
      // Return empty permissions structure if no service
      return {
        tasks: { create: false, read: false, update: false, delete: false },
        documents: { create: false, read: false, update: false, delete: false },
        calendar: { create: false, read: false, update: false, delete: false },
        users: { create: false, read: false, update: false, delete: false },
        agents: { execute: false },
        feedback: { read: false, create: false, updateOwn: false, resolve: false, manage: false, export: false },
        views: {
          dashboard: false,
          kanban: false,
          gantt: false,
          calendar: false,
          docs: false,
          reports: false,
          agentOffice: false,
          upgrade: false,
          settings: false,
          admin: false,
        },
        system: {
          import: false,
          export: false,
          history: false,
          search: false,
          customize: false,
        },
      };
    }
    return permissionService.getAllPermissions();
  };

  const value: AuthContextType = {
    user,
    actualUser,
    viewAsUser,
    viewAsUserId,
    viewAsOptions,
    isViewingAs,
    isAuthenticated,
    isLoading,
    setViewAsUserId,
    clearViewAs,
    login,
    signup,
    updateSelfServicePlan,
    logout,
    hasPermission,
    canAccessView,
    validateOperation,
    shouldShowUIElement,
    getAllPermissions,
    permissionService,
    hasViewPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
